import React, { useEffect, useRef } from 'react';
import {
  Accordion,
  ActionIcon,
  Badge,
  Box,
  Center,
  Group,
  Image,
  Loader,
  rem,
  ScrollArea,
  SimpleGrid,
  Stack,
  Text,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import {
  IconFolder,
  IconPhoto,
  IconRefresh,
  IconVideo,
} from '@tabler/icons-react';
import type { LocationMetadata } from '../types/LocationMetadata';

// ─── Public types ────────────────────────────────────────────────────────────

/**
 * A single media item with a unique identifier, name, optional thumbnail URL, and a video flag.
 */
export interface MediaItem {
  /** Unique identifier (numeric id or string driveItem id). */
  id: string;
  name: string;
  thumbnailUrl?: string;
  isVideo: boolean;
  metadata?: LocationMetadata;
}

/**
 * A named group of media items with optional sub-groups, used to render items as an accordion.
 */
export interface MediaGroup {
  key: string;
  label: string;
  items: MediaItem[];
  subGroups?: MediaGroup[];
}

/**
 * Props for the MediaGallery component that renders media items as a thumbnail grid or accordion.
 */
export interface MediaGalleryProps {
  /** Flat list of items — used when there is no grouping. */
  items?: MediaItem[];
  /** Grouped items — renders as an Accordion. Takes precedence over `items`. */
  groups?: MediaGroup[];
  isLoading: boolean;
  isError?: boolean;
  errorMessage?: string;
  emptyMessage?: string;
  onRefresh?: () => void;
  /** Rendered in the toolbar next to the refresh button. */
  uploadAction?: React.ReactNode;
  /** Callback when a thumbnail is clicked — opens the lightbox. */
  onItemClick?: (item: MediaItem) => void;
  /** Reports a hovered or keyboard-focused thumbnail; null clears the highlight. */
  onItemHover?: (item: MediaItem | null) => void;
  /** Max number of columns in the thumbnail grid. Default 3. */
  maxColumns?: number;
}

// ─── Internal sub-components ─────────────────────────────────────────────────

const THUMB_HEIGHT = 140;

/**
 * Internal component that renders a single thumbnail with a hover tooltip, showing an image or a video placeholder icon.
 */
function Thumbnail({
  item,
  onClick,
  onHover,
}: {
  item: MediaItem;
  onClick?: (item: MediaItem) => void;
  onHover?: (item: MediaItem | null) => void;
}) {
  const hovered = useRef(false);
  const focused = useRef(false);
  const latestHover = useRef(onHover);
  useEffect(() => { latestHover.current = onHover; }, [onHover]);
  useEffect(() => () => {
    if (hovered.current || focused.current) latestHover.current?.(null);
  }, [item.id]);
  return (
    <Tooltip label={item.name} position="bottom" withinPortal>
      <UnstyledButton
        onClick={() => onClick?.(item)}
        onPointerEnter={(event) => {
          if (event.pointerType === 'touch') return;
          hovered.current = true;
          onHover?.(item);
        }}
        onPointerLeave={() => {
          hovered.current = false;
          if (!focused.current) onHover?.(null);
        }}
        onFocus={() => {
          focused.current = true;
          onHover?.(item);
        }}
        onBlur={() => {
          focused.current = false;
          if (!hovered.current) onHover?.(null);
        }}
        style={{ position: 'relative', borderRadius: 8, overflow: 'hidden' }}
      >
        {item.thumbnailUrl ? (
          <Image
            src={item.thumbnailUrl}
            alt={item.name}
            radius="md"
            h={rem(THUMB_HEIGHT)}
            w="100%"
            fit="cover"
          />
        ) : (
          <Center
            h={rem(THUMB_HEIGHT)}
            style={{
              border: '1px solid var(--mantine-color-gray-4)',
              borderRadius: 8,
            }}
          >
            {item.isVideo ? <IconVideo size={32} /> : <IconPhoto size={32} />}
          </Center>
        )}
        {/* Video overlay indicator */}
        {item.isVideo && item.thumbnailUrl && (
          <Center
            style={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
              background: 'rgba(0,0,0,0.15)',
              borderRadius: 8,
            }}
          >
            <IconVideo size={24} color="white" />
          </Center>
        )}
      </UnstyledButton>
    </Tooltip>
  );
}

/**
 * Internal component that renders items as a SimpleGrid of Thumbnail cells with a configurable number of columns.
 */
function ThumbnailGrid({
  items,
  maxColumns,
  onItemClick,
  onItemHover,
}: {
  items: MediaItem[];
  maxColumns: number;
  onItemClick?: (item: MediaItem) => void;
  onItemHover?: (item: MediaItem | null) => void;
}) {
  if (items.length === 0) return null;
  return (
    <SimpleGrid cols={Math.min(maxColumns, items.length)} spacing="xs">
      {items.map((item) => (
        <Thumbnail key={item.id} item={item} onClick={onItemClick} onHover={onItemHover} />
      ))}
    </SimpleGrid>
  );
}

/**
 * Internal component that renders grouped items as an Accordion with folder headers and item counts per section.
 */
function GroupAccordion({
  groups,
  maxColumns,
  onItemClick,
  onItemHover,
}: {
  groups: MediaGroup[];
  maxColumns: number;
  onItemClick?: (item: MediaItem) => void;
  onItemHover?: (item: MediaItem | null) => void;
}) {
  return (
    <Accordion multiple variant="separated" radius="md">
      {groups.map((group) => {
        const totalCount =
          group.items.length +
          (group.subGroups?.reduce((s, sg) => s + sg.items.length, 0) ?? 0);
        return (
          <Accordion.Item key={group.key} value={group.key}>
            <Accordion.Control>
              <Group gap="xs" wrap="nowrap">
                <IconFolder size={16} />
                <Text fz="sm" fw={500} lineClamp={1}>
                  {group.label}
                </Text>
                {totalCount > 0 && (
                  <Badge size="sm" variant="light" color="gray" circle>
                    {totalCount}
                  </Badge>
                )}
              </Group>
            </Accordion.Control>
            <Accordion.Panel>
              <Stack gap="sm">
                {group.items.length > 0 && (
                  <ThumbnailGrid items={group.items} maxColumns={maxColumns} onItemClick={onItemClick} onItemHover={onItemHover} />
                )}
                {group.subGroups?.map((sub) => (
                  <Accordion key={sub.key} multiple variant="default" radius="sm">
                    <Accordion.Item value={sub.key}>
                      <Accordion.Control>
                        <Group gap="xs" wrap="nowrap">
                          <Text fz="xs" fw={500} lineClamp={1}>
                            {sub.label}
                          </Text>
                          {sub.items.length > 0 && (
                            <Badge size="xs" variant="light" color="gray" circle>
                              {sub.items.length}
                            </Badge>
                          )}
                        </Group>
                      </Accordion.Control>
                      <Accordion.Panel>
                        <ThumbnailGrid items={sub.items} maxColumns={maxColumns} onItemClick={onItemClick} onItemHover={onItemHover} />
                      </Accordion.Panel>
                    </Accordion.Item>
                  </Accordion>
                ))}
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        );
      })}
    </Accordion>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

/**
 * Renders media items as a thumbnail grid or grouped accordion, with loading, error, and empty states, plus a toolbar with refresh and upload actions.
 */
export function MediaGallery({
  items,
  groups,
  isLoading,
  isError,
  errorMessage = 'Failed to load media',
  emptyMessage = 'No media yet.',
  onRefresh,
  uploadAction,
  onItemClick,
  maxColumns = 3,
  onItemHover,
}: MediaGalleryProps) {
  const hasGroups = groups && groups.length > 0;
  const hasItems = items && items.length > 0;
  const isEmpty = !hasGroups && !hasItems;
  const totalCount = hasGroups
    ? groups.reduce(
        (t, g) =>
          t + g.items.length + (g.subGroups?.reduce((s, sg) => s + sg.items.length, 0) ?? 0),
        0
      )
    : items?.length ?? 0;

  return (
    <Stack gap="xs" h="100%">
      {/* Toolbar */}
      <Group justify="space-between" px="sm" pt="xs">
        <Text fw={600} size="sm">
          Media{totalCount > 0 ? ` (${totalCount})` : ''}
        </Text>
        <Group gap="xs">
          {onRefresh && (
            <ActionIcon variant="subtle" onClick={onRefresh} aria-label="Refresh media">
              <IconRefresh size={18} />
            </ActionIcon>
          )}
          {uploadAction}
        </Group>
      </Group>

      {/* Loading */}
      {isLoading && (
        <Center mih={120}>
          <Loader />
        </Center>
      )}

      {/* Error */}
      {isError && !isLoading && (
        <Center mih={80}>
          <Text c="red" size="sm">
            {errorMessage}
          </Text>
        </Center>
      )}

      {/* Empty */}
      {!isLoading && !isError && isEmpty && (
        <Center mih={80}>
          <Text c="dimmed" size="sm">
            {emptyMessage}
          </Text>
        </Center>
      )}

      {/* Content */}
      {!isLoading && !isError && !isEmpty && (
        <ScrollArea style={{ flex: 1 }} px="sm" pb="sm">
          {hasGroups ? (
            <GroupAccordion groups={groups} maxColumns={maxColumns} onItemClick={onItemClick} onItemHover={onItemHover} />
          ) : (
            <ThumbnailGrid items={items ?? []} maxColumns={maxColumns} onItemClick={onItemClick} onItemHover={onItemHover} />
          )}
        </ScrollArea>
      )}
    </Stack>
  );
}
